import { useEffect, useState } from "react";
import { Info } from "lucide-react";
import { GAME_LEVELS, GAME_PLAY_MODES, getGameLevelDescription } from "../constants/gameLevels";
import { getKeyById, getKeyPowerPreposition, MIN_KEY_LEVEL } from "../constants/keys";
import {
  readKeyUsagePreference,
  subscribeKeyUsagePreference,
} from "../utils/keyUsagePreference";
import LockIcon from "./IconComponent/LockIcon";
import MenuIconTooltip from "./MenuIconTooltip";
import ToggleSwitch from "./ToggleSwitch";
import "./GameDifficultySelect.css";
import "./VocabTooltip.css";

const GAME_PLAY_MODE_TOOLTIP = (
  <>
    Mode S (standard) : la partie est enregistrée. Elle compte pour la
    progression des niveaux, les statistiques et les quêtes.
    <br />
    <br />
    Mode T (test) : tous les niveaux sont jouables, sans impact sur la maîtrise,
    les statistiques ou les titres. Chaque niveau n'y joue que ses propres mots
    (noyau au niveau 1, un seul paquet aux niveaux suivants). Les potions
    utilisées sont consommées ; les parchemins sont indisponibles. Les quêtes
    ne sont pas affectées, sauf l'objectif du jour qui demande de jouer le
    niveau supérieur en mode T.
  </>
);

const publicUrl = process.env.PUBLIC_URL || "";

const KEY_POWER_STARS = [
  { className: "game-difficulty-card__star--1" },
  { className: "game-difficulty-card__star--2" },
  { className: "game-difficulty-card__star--3" },
  { className: "game-difficulty-card__star--4" },
  { className: "game-difficulty-card__star--5" },
  { className: "game-difficulty-card__star--6" },
];

function KeyPowerStars() {
  return (
    <span className="game-difficulty-card__stars" aria-hidden="true">
      {KEY_POWER_STARS.map((star) => (
        <span key={star.className} className={`game-difficulty-card__star ${star.className}`} />
      ))}
    </span>
  );
}

export default function GameDifficultySelect({
  onSelect,
  loading,
  error,
  maxUnlockedLevel = 1,
  playMode = GAME_PLAY_MODES.STANDARD,
  onPlayModeChange,
}) {
  const [selectedKeyId, setSelectedKeyId] = useState(() => readKeyUsagePreference());
  const selectedKey = selectedKeyId ? getKeyById(selectedKeyId) : null;
  const isTestPlay = playMode === GAME_PLAY_MODES.TEST;
  const keyAppliesToCurrentLevel =
    !isTestPlay && Boolean(selectedKey) && maxUnlockedLevel >= MIN_KEY_LEVEL;

  useEffect(() => subscribeKeyUsagePreference(setSelectedKeyId), []);

  return (
    <div className="game-difficulty">
      <header className="game-difficulty-header">
        <h2 className="game-difficulty-title">Jeu de vocabulaire</h2>
        <div className="game-difficulty-header__mode">
          <ToggleSwitch
            compact
            alwaysUncheckedLook
            className="game-difficulty-mode-switch"
            checked={isTestPlay}
            uncheckedSymbol="S"
            checkedSymbol="T"
            disabled={loading}
            ariaLabel={
              isTestPlay
                ? "Mode test — basculer vers le mode standard"
                : "Mode standard — basculer vers le mode test"
            }
            onChange={(checked) => {
              onPlayModeChange?.(
                checked ? GAME_PLAY_MODES.TEST : GAME_PLAY_MODES.STANDARD
              );
            }}
          />
          <MenuIconTooltip tooltip={GAME_PLAY_MODE_TOOLTIP} placement="below" portal>
            <button
              type="button"
              className="vocab-info-button"
              aria-label="Explication des modes de jeu standard et test"
            >
              <Info size={13} strokeWidth={2} aria-hidden="true" />
            </button>
          </MenuIconTooltip>
        </div>
      </header>

      {error && <p className="game-difficulty-error">{error}</p>}

      <div className="game-difficulty-grid">
        {GAME_LEVELS.map((level) => {
          const isLocked = !isTestPlay && level.id > maxUnlockedLevel;
          const usesKeyPower =
            keyAppliesToCurrentLevel && level.id === maxUnlockedLevel;

          return (
            <button
              key={level.id}
              type="button"
              className={`game-difficulty-card${
                isLocked ? " game-difficulty-card--locked" : ""
              }${usesKeyPower ? " game-difficulty-card--key-powered" : ""}`}
              disabled={loading || isLocked}
              onClick={() => onSelect(level.id)}
              aria-label={
                isLocked
                  ? `Niveau ${level.id} — ${level.name} (verrouillé)`
                  : `Niveau ${level.id} — ${level.name}`
              }
              aria-disabled={isLocked || undefined}
            >
              {usesKeyPower && <KeyPowerStars />}

              {isLocked && (
                <span className="game-difficulty-card__lock" aria-hidden="true">
                  <LockIcon size={18} color="#39210d" strokeWidth={1.8} />
                </span>
              )}

              <img
                src={`${publicUrl}/icons/${level.icon}`}
                alt=""
                className="game-difficulty-card__icon"
                loading="lazy"
                draggable={false}
                aria-hidden="true"
              />

              <div className="game-difficulty-card__content">
                <span className="game-difficulty-level-num">Niveau {level.id}</span>
                <span className="game-difficulty-name">{level.name}</span>
                <span className="game-difficulty-desc">
                  {getGameLevelDescription(level, { testPlay: isTestPlay })}
                </span>
                {usesKeyPower && (
                  <span className="game-difficulty-key-note">
                    Vous utiliserez le pouvoir {getKeyPowerPreposition(selectedKey.name)}
                    <strong>{selectedKey.name}</strong> pour cette partie.
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
